import type { AdminOrderResponse, AdminReviveCheck } from '@inflip/contracts/admin';
import { DepositReturnKind } from '@inflip/shared';
import { useEffect, useId, useState } from 'react';

import {
  type BankAccountInput,
  ReturnFields,
  refundBankOf,
  bankOk,
  sameDepositor,
  trimBank,
  won,
} from './confirm-deposit-dialog';
import { Button, Dialog, DialogFooter, inputCls } from './ui';
import { api } from '../lib/api-client';

/**
 * 취소된 주문에 늦게 들어온 입금 처리 (docs/deposit-exceptions-design.md).
 * 기한 만료로 시스템이 취소했고 되살리기 검사를 통과하면 되살린다(결제 확인됨). 걸리거나 금액이 적으면 받은 금액을 돌려준다.
 */
export function LateDepositDialog({
  order,
  token,
  busy,
  error,
  onRevive,
  onReturn,
  onClose,
}: {
  order: AdminOrderResponse;
  token: string;
  busy: boolean;
  error: string | null;
  onRevive(body: Parameters<typeof api.reviveOrder>[2]): void;
  onReturn(body: Parameters<typeof api.returnDeposit>[2]): void;
  onClose(): void;
}) {
  const ids = { amount: useId(), name: useId(), reason: useId(), override: useId() };
  const [check, setCheck] = useState<AdminReviveCheck | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [amountText, setAmountText] = useState('');
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [override, setOverride] = useState('');
  const [returnReason, setReturnReason] = useState('');
  // 구매자가 등록한 환불 계좌로 미리 채운다
  const [bank, setBank] = useState<BankAccountInput>(refundBankOf(order.buyerRefundAccount));
  const accountLocked = Boolean(order.buyerRefundAccount);

  useEffect(() => {
    api
      .reviveCheck(token, order.id)
      .then(setCheck)
      .catch((e: unknown) => setLoadError(e instanceof Error ? e.message : String(e)));
  }, [token, order.id]);

  const expected = check?.payment?.amount ?? order.payment?.amount ?? 0;
  const expectedDepositor =
    check?.payment?.depositReference ?? order.payment?.depositReference ?? null;
  const amount = Number(amountText || 0);
  const entered = amount > 0;
  const revivable = !!check && check.blockers.length === 0;
  // 되살리기: 검사 통과 + 받을 금액 이상. 아니면 받은 금액 전부 반환
  const reviving = revivable && entered && amount >= expected;
  const over = reviving && amount > expected;
  const nameMismatch =
    !!name.trim() && !!expectedDepositor && !sameDepositor(name, expectedDepositor);
  const returnOk = returnReason.trim().length >= 5 && bankOk(bank);

  const canRevive =
    !busy &&
    reviving &&
    !!name.trim() &&
    reason.trim().length >= 5 &&
    (!nameMismatch || override.trim().length >= 5) &&
    (!over || returnOk);
  const canReturn = !busy && !!check && entered && !reviving && !!name.trim() && returnOk;

  return (
    <Dialog label="늦게 들어온 입금 처리" title="늦게 들어온 입금 처리" onClose={onClose} wide>
      <p className="text-sm text-muted-foreground">
        취소된 주문에 들어온 입금이에요. 기한 만료로 취소됐고 지금도 살 수 있는 주문이면 되살려
        결제를 확인하고, 아니면 받은 금액을 돌려줘요.
      </p>

      {loadError && (
        <p role="alert" className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {loadError}
        </p>
      )}

      <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-md bg-muted px-3.5 py-3 text-sm">
        <dt className="text-muted-foreground">받을 금액</dt>
        <dd className="text-right font-semibold tabular-nums">
          {won(expected)}
          {check && check.orderIds.length > 1 && ` (묶음 ${check.orderIds.length}건)`}
        </dd>
        <dt className="text-muted-foreground">안내한 입금자명</dt>
        <dd className="text-right font-semibold">{expectedDepositor ?? '—'}</dd>
      </dl>

      {check && (
        <div
          className={`mt-3 rounded-md px-3.5 py-3 text-sm ${revivable ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}
        >
          {revivable ? (
            <p className="font-semibold">
              되살릴 수 있어요{check.orderIds.length > 1 ? ' — 묶음 전체가 함께 되살아나요' : ''}.
            </p>
          ) : (
            <>
              <p className="font-semibold">되살릴 수 없어요. 받은 금액을 돌려줘 주세요.</p>
              <ul className="mt-1 list-disc pl-5 text-xs leading-relaxed">
                {check.blockers.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <div className="mt-4 space-y-3">
        <label htmlFor={ids.amount} className="block">
          <span className="text-xs font-semibold text-muted-foreground">실제 입금액 (원)</span>
          <input
            id={ids.amount}
            inputMode="numeric"
            className={`mt-1 w-full tabular-nums ${inputCls}`}
            placeholder={expected.toLocaleString('ko-KR')}
            value={amountText ? Number(amountText).toLocaleString('ko-KR') : ''}
            onChange={(e) => setAmountText(e.target.value.replace(/[^\d]/g, ''))}
          />
          {revivable && entered && amount < expected && (
            <span className="mt-1 block text-xs font-semibold text-rose-600">
              {won(expected - amount)} 적게 들어와 되살릴 수 없어요. 받은 금액을 돌려주고 다시
              주문하도록 안내해 주세요.
            </span>
          )}
        </label>
        <label htmlFor={ids.name} className="block">
          <span className="text-xs font-semibold text-muted-foreground">은행 입금자명</span>
          <input
            id={ids.name}
            className={`mt-1 w-full ${inputCls}`}
            placeholder={expectedDepositor ?? '입금자명'}
            maxLength={64}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        {reviving && (
          <>
            {nameMismatch && (
              <label htmlFor={ids.override} className="block">
                <span className="text-xs font-semibold text-rose-600">
                  안내한 입금자명과 달라요 — 본인 입금 확인 근거 (5자 이상)
                </span>
                <textarea
                  id={ids.override}
                  rows={2}
                  maxLength={500}
                  className={`mt-1 w-full resize-none py-2 ${inputCls}`}
                  value={override}
                  onChange={(e) => setOverride(e.target.value)}
                />
              </label>
            )}
            <label htmlFor={ids.reason} className="block">
              <span className="text-xs font-semibold text-muted-foreground">
                되살리는 사유 (5자 이상, 기록에 남아요)
              </span>
              <textarea
                id={ids.reason}
                rows={2}
                maxLength={500}
                className={`mt-1 w-full resize-none py-2 ${inputCls}`}
                placeholder="예: 기한 다음 날 오전 입금, 구매자 확인 후 거래 진행 희망"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
            {over && (
              <div className="space-y-2.5 rounded-md bg-amber-50 px-3.5 py-3">
                <p className="text-sm font-semibold text-amber-800">
                  {won(amount - expected)} 많이 들어왔어요 — 넘친 금액은 돌려줘요
                </p>
                <ReturnFields
                  reason={returnReason}
                  onReason={setReturnReason}
                  bank={bank}
                  onBank={setBank}
                  accountLocked={accountLocked}
                  reasonLabel="초과분 반환 사유 (5자 이상)"
                  reasonPlaceholder="예: 금액을 잘못 입력해 더 보냄"
                />
              </div>
            )}
          </>
        )}

        {check && entered && !reviving && (
          <div className="rounded-md bg-red-50 px-3.5 py-3">
            <p className="mb-2 text-sm font-semibold text-red-700">받은 {won(amount)}을 돌려줘요</p>
            <ReturnFields
              reason={returnReason}
              onReason={setReturnReason}
              bank={bank}
              onBank={setBank}
              accountLocked={accountLocked}
              reasonPlaceholder="예: 기한 만료 뒤 입금, 권리가 판매 중지돼 되살릴 수 없음"
            />
          </div>
        )}
      </div>

      <DialogFooter
        notice={
          error && (
            <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )
        }
      >
        <Button variant="secondary" className="flex-1" onClick={onClose}>
          닫기
        </Button>
        {reviving ? (
          <Button
            className="flex-1"
            disabled={!canRevive}
            onClick={() =>
              onRevive({
                receivedAmount: amount,
                depositReference: name.trim(),
                reason: reason.trim(),
                ...(nameMismatch ? { depositorOverrideReason: override.trim() } : {}),
                ...(over
                  ? { overpayment: { reason: returnReason.trim(), bankAccount: trimBank(bank) } }
                  : {}),
              })
            }
          >
            되살리고 결제 확인
          </Button>
        ) : (
          <Button
            variant="danger"
            className="flex-1"
            disabled={!canReturn}
            onClick={() =>
              onReturn({
                kind: DepositReturnKind.LATE_DEPOSIT,
                receivedAmount: amount,
                depositorName: name.trim(),
                reason: returnReason.trim(),
                bankAccount: trimBank(bank),
              })
            }
          >
            받은 금액 반환
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
}
